import { useState, type ReactNode } from "react";
import { cn } from "@vseh/ui";
import { AgentAvatar } from "./AgentAvatar";

/**
 * Сообщение от AI-агента в диалоге. По §14 «Agent chat bubble».
 *
 *  - Левый бордер ai-500 + лёгкий фон ai-50 — визуально отличается
 *    от человеческих сообщений (которые на base-100, без border).
 *  - Под баблом мета-строка: модель · латенси · стоимость · ссылка
 *    на свёрнутые tool calls.
 *  - children — основное содержимое (StreamingText, обычный текст,
 *    результаты с чипами действий).
 */
export interface AgentBubbleMeta {
  model?: string;
  latencyMs?: number;
  costUsd?: number;
}

export interface AgentBubbleProps {
  /** Имя/роль агента (показывается над контентом). */
  agentLabel?: string;
  meta?: AgentBubbleMeta;
  /** Свёрнутые tool calls — обычно массив <ToolCallCard />. */
  tools?: ReactNode;
  toolCount?: number;
  children: ReactNode;
  className?: string;
}

function formatUsd(value: number): string {
  return value < 0.01 ? `$${value.toFixed(4)}` : `$${value.toFixed(3)}`;
}

export function AgentBubble({
  agentLabel,
  meta,
  tools,
  toolCount,
  children,
  className,
}: AgentBubbleProps): JSX.Element {
  const resolvedToolCount = toolCount ?? (Array.isArray(tools) ? tools.length : tools ? 1 : 0);
  const [toolsOpen, setToolsOpen] = useState(false);
  const hasTools = resolvedToolCount > 0;

  return (
    <div className={cn("flex items-start gap-3", className)}>
      <AgentAvatar size="md" />
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            "rounded-lg border-l-2 border-ai-500 bg-ai-50",
            "px-4 py-3",
          )}
        >
          {agentLabel && (
            <div className="mb-1 text-2xs font-medium uppercase tracking-wide text-ai-800">
              {agentLabel}
            </div>
          )}
          <div className="text-md text-base-900">{children}</div>
        </div>

        {(meta || hasTools) && (
          <div className="mt-1.5 flex items-center gap-2 px-1 text-2xs text-base-500">
            {meta?.model && <span className="font-mono">{meta.model}</span>}
            {meta?.latencyMs != null && (
              <>
                {meta.model && <span aria-hidden="true">·</span>}
                <span className="tabular-nums">{(meta.latencyMs / 1000).toFixed(1)}s</span>
              </>
            )}
            {meta?.costUsd != null && (
              <>
                <span aria-hidden="true">·</span>
                <span className="tabular-nums">{formatUsd(meta.costUsd)}</span>
              </>
            )}
            {hasTools && (
              <>
                <span aria-hidden="true">·</span>
                <button
                  type="button"
                  onClick={() => setToolsOpen((v) => !v)}
                  className="hover:underline"
                >
                  {toolsOpen ? "скрыть" : "показать"} инструменты ({resolvedToolCount})
                </button>
              </>
            )}
          </div>
        )}

        {hasTools && toolsOpen && (
          <div className="mt-2 space-y-2">{tools}</div>
        )}
      </div>
    </div>
  );
}

/**
 * Сообщение от человека. Зеркальная пара к AgentBubble — нужна, чтобы
 * визуальный язык чата был согласован.
 */
export interface UserBubbleProps {
  children: ReactNode;
  timestamp?: string;
  className?: string;
}

export function UserBubble({ children, timestamp, className }: UserBubbleProps): JSX.Element {
  return (
    <div className={cn("flex flex-col items-end gap-1", className)}>
      <div className="max-w-[80%] rounded-lg bg-base-100 px-4 py-3 text-md text-base-900">
        {children}
      </div>
      {timestamp && (
        <span className="text-2xs text-base-500 tabular-nums">{timestamp}</span>
      )}
    </div>
  );
}
